import React, {
  useState,
  useEffect,
  useContext,
  useMemo,
  useCallback,
} from "react";
import { useNavigate } from "react-router-dom";
import Table from "../../../core/common/dataTable/index";
import CollapseHeader from "../../../core/common/collapse-header";

import { TenantSubscription } from "./subscription.service";
import { useSubscriptions } from "../../../hooks/useSubscriptions";
import { useTenantChange } from "../../../hooks/useTenantChange";
import { AuthContext } from "../../../context/authcontext";
import TableFilters, {
  FilterState,
  FilterConfig,
} from "../../../components/filters/TableFilters";
import SortableHeader from "../../../components/filters/SortableHeader";
import TablePagination from "../../../components/pagination/TablePagination";
import { all_routes } from "../../router/all_routes";

const SubscriptionList = () => {
  const navigate = useNavigate();
  const { selectedTenantId, auth, setSelectedTenantId } =
    useContext(AuthContext);

  const selectedTenant = auth?.tenants?.find(
    (t: any) => String(t.tenant_id) === String(selectedTenantId)
  );
  const userRole = selectedTenant?.role_key || selectedTenant?.role_name || "";
  const isSuperAdmin =
    userRole?.toLowerCase() === "superadmin" ||
    userRole?.toLowerCase() === "super_admin" ||
    userRole?.toLowerCase() === "platform_analyst" ||
    userRole?.toLowerCase() === "platformanalyst" ||
    userRole?.toLowerCase() === "apiadmin" ||
    userRole?.toLowerCase() === "api_admin";
  const {
    subscriptions,
    loading,
    backgroundLoading,
    error,
    pagination,
    fetchSubscriptions,
    isInitialized,
    clearBackgroundLoading,
    clearSubscriptionsData,
  } = useSubscriptions();

  const [filters, setFilters] = useState<FilterState>({
    searchTerm: "",
    dateRange: [null, null] as [Date | null, Date | null],
    industryFilter: [],
    countryFilter: [],
    statusFilter: null,
    sortBy: "created_datetime",
    sortOrder: "desc",
  });

  const [filteredSubscriptionsData, setFilteredSubscriptionsData] = useState<
    any[]
  >([]);
  const [isFilterActive, setIsFilterActive] = useState(false);
  const [isFilterLoading, setIsFilterLoading] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);

  const buildApiParams = useCallback((currentFilters: FilterState) => {
    const params: Record<string, any> = {};

    if (currentFilters.searchTerm) {
      params.tenant = currentFilters.searchTerm;
    }

    if (currentFilters.dateRange[0]) {
      params.created_datetime_gte = currentFilters.dateRange[0]
        .toISOString()
        .split("T")[0];
    }
    if (currentFilters.dateRange[1]) {
      params.created_datetime_lte = currentFilters.dateRange[1]
        .toISOString()
        .split("T")[0];
    }

    if (currentFilters.statusFilter !== null) {
      params.is_active = currentFilters.statusFilter;
    }

    return params;
  }, []);

  useTenantChange({
    onTenantChange: () => {
      clearSubscriptionsData();
      setFilteredSubscriptionsData([]);
      setIsFilterActive(false);
      setCurrentPage(1);
    },
    refreshData: useCallback(() => {
      const params = buildApiParams(filters);
      fetchSubscriptions(1, 12, params);
    }, [buildApiParams, filters, fetchSubscriptions]),
    clearData: clearSubscriptionsData,
  });

  const filterConfig: FilterConfig = useMemo(
    () => ({
      showSearch: true,
      searchField: "tenant",
      showDateFilter: true,
      dateField: "created_datetime",
      showIndustryFilter: false,
      showCountryFilter: false,
      showStatusFilter: true,
      pageName: "subscription" as const,
      defaultSort: {
        field: "created_datetime",
        order: "desc" as const,
      },
    }),
    []
  );

  const handleFilterChange = useCallback(
    (newFilters: FilterState) => {
      setFilters(newFilters);
      setCurrentPage(1);

      const hasActiveFilters =
        newFilters.searchTerm ||
        newFilters.dateRange[0] ||
        newFilters.dateRange[1] ||
        newFilters.statusFilter !== null;

      if (!hasActiveFilters) {
        setIsFilterActive(false);
        setFilteredSubscriptionsData([]);
        const params = buildApiParams(newFilters);
        fetchSubscriptions(1, 12, params);
      }
    },
    [buildApiParams, fetchSubscriptions]
  );

  const handleApiFilterChange = useCallback(
    (apiFilters: Record<string, any>) => { },
    []
  );

  const handleFiltersReset = useCallback(() => {
    setIsFilterActive(false);
    setFilteredSubscriptionsData([]);
    setCurrentPage(1);
    const defaultFilters = {
      searchTerm: "",
      dateRange: [null, null] as [Date | null, Date | null],
      industryFilter: [],
      countryFilter: [],
      statusFilter: null,
      sortBy: "created_datetime",
      sortOrder: "desc" as const,
    };
    setFilters(defaultFilters);
    const params = buildApiParams(defaultFilters);
    fetchSubscriptions(1, 12, params);
  }, [fetchSubscriptions, buildApiParams]);

  const handlePageChange = useCallback(
    (page: number) => {
      setCurrentPage(page);
      const params = buildApiParams(filters);
      fetchSubscriptions(page, 12, params);
    },
    [buildApiParams, filters, fetchSubscriptions]
  );

  const handleApiCall = useCallback(
    async (url: string) => {
      try {
        setIsFilterLoading(true);

        const urlObj = new URL(url);
        const params: Record<string, any> = {};

        urlObj.searchParams.forEach((value, key) => {
          if (key !== "page" && key !== "page_size") {
            params[key] = value;
          }
        });

        await fetchSubscriptions(1, 12, params);
        setIsFilterActive(true);
        setCurrentPage(1);
      } catch (error: any) {
        console.error("Filter API error:", error);

        if (error.response?.status === 401 || error.response?.status === 403) {
          localStorage.removeItem("authCred");
          localStorage.removeItem("authResponse");
          localStorage.removeItem("authTimestamp");
          localStorage.removeItem("selectedTenantId");

          navigate("/login");
          return;
        }

        setIsFilterActive(false);
      } finally {
        setIsFilterLoading(false);
      }
    },
    [navigate, fetchSubscriptions]
  );

  const handleSortChange = useCallback(
    (newSortBy: string, newOrder: "asc" | "desc") => {
      const newFilters = {
        ...filters,
        sortBy: newSortBy,
        sortOrder: newOrder,
      };
      setFilters(newFilters);
      setCurrentPage(1);
    },
    [filters]
  );

  const handleTenantClick = useCallback(
    (tenantId: string) => {
      setSelectedTenantId(tenantId);
      localStorage.setItem("selectedTenantId", tenantId);
      navigate(all_routes.accessManagement);
    },
    [setSelectedTenantId, navigate]
  );

  const displaySubscriptions = useMemo(() => {
    let dataToSort = Array.isArray(subscriptions) ? [...subscriptions] : [];

    if (dataToSort.length > 0 && filters.sortBy) {
      dataToSort.sort((a, b) => {
        const { sortBy, sortOrder } = filters;
        let aValue = a[sortBy as keyof typeof a];
        let bValue = b[sortBy as keyof typeof b];

        if (aValue == null && bValue == null) return 0;
        if (aValue == null) return 1;
        if (bValue == null) return -1;

        if (typeof aValue === "string" && typeof bValue === "string") {
          if (sortBy === "created_datetime" || sortBy === "subscription_end_date") {
            const dateA = new Date(aValue).getTime();
            const dateB = new Date(bValue).getTime();
            return sortOrder === "asc" ? dateA - dateB : dateB - dateA;
          }
          const comparison = aValue.toLowerCase().localeCompare(bValue.toLowerCase(), undefined, { numeric: true });
          return sortOrder === "asc" ? comparison : -comparison;
        }

        if (typeof aValue === "number" && typeof bValue === "number") {
          return sortOrder === "asc" ? aValue - bValue : bValue - aValue;
        }

        if (typeof aValue === "boolean" && typeof bValue === "boolean") {
          return sortOrder === "asc" 
            ? (aValue === bValue ? 0 : aValue ? -1 : 1)
            : (aValue === bValue ? 0 : aValue ? 1 : -1);
        }

        const aStr = String(aValue).toLowerCase();
        const bStr = String(bValue).toLowerCase();
        const comparison = aStr.localeCompare(bStr);
        return sortOrder === "asc" ? comparison : -comparison;
      });
    }

    return dataToSort;
  }, [subscriptions, filters.sortBy, filters.sortOrder]);

  useEffect(() => {
    if (!isInitialized) {
      const params = buildApiParams(filters);
      fetchSubscriptions(1, 12, params);
    }
  }, [isInitialized, fetchSubscriptions, buildApiParams, filters]);

  useEffect(() => {
    if (isInitialized && backgroundLoading) {
      const fetchBackgroundData = async () => {
        try {
        } catch (error) {
          console.error("Background API error:", error);
        } finally {
          clearBackgroundLoading();
        }
      };

      fetchBackgroundData();
    }
  }, [isInitialized, backgroundLoading, clearBackgroundLoading]);

  useEffect(() => {
    if (pagination.currentPage && pagination.currentPage !== currentPage) {
      setCurrentPage(pagination.currentPage);
    }
  }, [pagination.currentPage, currentPage]);

  const getStatusBadge = (subscription: TenantSubscription) => {
    if (subscription.is_expired) {
      return <span className="badge bg-danger">Expired</span>;
    }
    if (subscription.is_active) {
      return <span className="badge bg-success">Active</span>;
    }
    return <span className="badge bg-secondary">Inactive</span>;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      timeZone: "UTC",
    });
  };

  const columns = [
    {
      title: (
        <SortableHeader
          title="Tenant ID"
          field="tenant_id"
          currentSort={filters.sortBy}
          currentOrder={filters.sortOrder}
          onSort={handleSortChange}
          className="justify-content-center"
        />
      ),
      dataIndex: "tenant_id",
      render: (text: string, record: TenantSubscription) => (
        <div className="text-center">
          <span className="fw-medium text-muted">{record.tenant_id}</span>
        </div>
      ),
    },
    {
      title: "Tenant Name",
      dataIndex: "tenant_name",
      render: (text: string, record: TenantSubscription) => (
        <div
          className="d-flex flex-column cursor-pointer"
          onClick={() => handleTenantClick(record.tenant_id)}
          style={{ cursor: "pointer" }}
        >
          <h6 className="mb-1">{record.tenant?.tenant_name || record.tenant_name}</h6>
          <span className="text-muted small">{record.tenant?.domain_name || record.domain_name}</span>
        </div>
      ),
    },
    {
      title: "Subscription Plan",
      dataIndex: "subscription_display_name",
      render: (text: string, record: TenantSubscription) => (
        <div className="d-flex flex-column">
          <span className="fw-medium">{record.subscription_display_name}</span>
        </div>
      ),
    },
    {
      title: (
        <SortableHeader
          title="Billing Cycle"
          field="interval_display_name"
          currentSort={filters.sortBy}
          currentOrder={filters.sortOrder}
          onSort={handleSortChange}
        />
      ),
      dataIndex: "interval_display_name",
      render: (text: string, record: TenantSubscription) => (
        <div>
          {record.interval_length} {record.interval_display_name}
          {record.interval_length !== "1" ? "s" : ""}
        </div>
      ),
    },
    {
      title: (
        <SortableHeader
          title="Created Date"
          field="created_datetime"
          currentSort={filters.sortBy}
          currentOrder={filters.sortOrder}
          onSort={handleSortChange}
        />
      ),
      dataIndex: "created_datetime",
      render: (text: string, record: TenantSubscription) => (
        <div>{formatDate(record.created_datetime)}</div>
      ),
    },
    {
      title: (
        <SortableHeader
          title="Expires On"
          field="subscription_end_date"
          currentSort={filters.sortBy}
          currentOrder={filters.sortOrder}
          onSort={handleSortChange}
        />
      ),
      dataIndex: "subscription_end_date",
      render: (text: string, record: TenantSubscription) => (
        <div>{formatDate(record.subscription_end_date)}</div>
      ),
    },
    {
      title: (
        <SortableHeader
          title="Users"
          field="number_of_user"
          currentSort={filters.sortBy}
          currentOrder={filters.sortOrder}
          onSort={handleSortChange}
        />
      ),
      dataIndex: "number_of_user",
      render: (text: number, record: TenantSubscription) => (
        <div className="text-center">
          <span className="fw-medium">{record.number_of_user}</span>
          {record.max_user && (
            <span className="text-muted">/{record.max_user}</span>
          )}
        </div>
      ),
    },
    {
      title: (
        <SortableHeader
          title="Status"
          field="is_active"
          currentSort={filters.sortBy}
          currentOrder={filters.sortOrder}
          onSort={handleSortChange}
        />
      ),
      dataIndex: "status",
      render: (text: string, record: TenantSubscription) =>
        getStatusBadge(record),
    },
  ];

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="row">
          <div className="col-md-12">
            <div className="page-header">
              <div className="row align-items-center">
                <div className="col-8">
                  <h4 className="page-title">
                    Tenant Subscriptions
                    <span className="count-title">{pagination.totalCount}</span>
                  </h4>
                </div>
                <div className="col-4 text-end">
                  <div className="head-icons">
                    <CollapseHeader />
                  </div>
                </div>
              </div>
            </div>

            <div className="card" style={{ position: "relative" }}>
              {loading && (
                <div
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: "rgba(255, 255, 255)",
                    zIndex: 1000,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "not-allowed",
                  }}
                >
                  <div className="text-center">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading</span>
                    </div>
                    <div className="mt-2">
                      <small className="text-muted">
                        Loading Subscriptions
                      </small>
                    </div>
                  </div>
                </div>
              )}

              <div className="card-body">
                <div className="d-flex align-items-center justify-content-between flex-wrap row-gap-2 mb-4">
                  <div className="d-flex align-items-center flex-wrap row-gap-2">
                    <TableFilters
                      config={filterConfig}
                      data={subscriptions}
                      onFilterChange={handleFilterChange}
                      onApiFilterChange={handleApiFilterChange}
                      onApiCall={handleApiCall}
                      onFiltersReset={handleFiltersReset}
                      tenantId={undefined}
                      className=""
                    />
                  </div>
                </div>
              </div>

              {loading ? (
                <div className="card-body">
                  <div className="text-center py-4">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <div className="mt-2">
                      <small className="text-muted">
                        Loading Subscriptions...
                      </small>
                    </div>
                  </div>
                </div>
              ) : error ? (
                <div className="card-body">
                  <div className="alert alert-danger text-center">
                    <i className="ti ti-alert-circle me-2"></i>
                    {error}
                  </div>
                </div>
              ) : (
                <div className="card-body">
                  <div
                    className="table-responsive contact-table-wrapper"
                    style={{ position: "relative" }}
                  >
                    {isFilterLoading && (
                      <div
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          backgroundColor: "rgba(255, 255, 255, 1)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          zIndex: 10,
                          minHeight: "200px",
                        }}
                      >
                        <div className="d-flex align-items-center">
                          <div
                            className="spinner-border spinner-border-sm me-2"
                            role="status"
                          >
                            <span className="visually-hidden">Loading...</span>
                          </div>
                          <span>Filtering results...</span>
                        </div>
                      </div>
                    )}
                    {displaySubscriptions.length === 0 ? (
                      <div className="text-center py-5">
                        <div className="avatar avatar-xl bg-light-gray rounded-circle mb-3 mx-auto">
                          <i className="fa fa-exclamation fs-36 text-gray"></i>
                        </div>
                        <h4 className="text-gray mb-2">
                          No Subscriptions Found!
                        </h4>
                        <p className="text-muted mb-3">
                          There are no subscriptions to display at the moment.
                        </p>
                      </div>
                    ) : (
                      <>
                        <Table
                          columns={columns}
                          dataSource={displaySubscriptions}
                          rowKey={(record: TenantSubscription) =>
                            record.tenant_subscription_id || 0
                          }
                          className="table table-striped contact-table"
                          pagination={false}
                          rowSelection={false}
                        />
                        <TablePagination
                          currentPage={pagination.currentPage || currentPage}
                          pageSize={12}
                          totalItems={pagination.totalCount}
                          filteredItems={pagination.totalCount}
                          isFiltered={
                            filters.searchTerm !== "" ||
                            filters.dateRange[0] !== null ||
                            filters.dateRange[1] !== null ||
                            filters.statusFilter !== null
                          }
                          onPageChange={handlePageChange}
                          onPageSizeChange={() => { }}
                          entityName="Subscriptions"
                          loading={loading}
                        />
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionList;
